import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sdkRequest } from '@eventbrite/legacy-infra.http';

import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';

import { transformTrackingBeacons, type TrackingBeaconsResponse, type TrackingBeacons } from './transform';

const getTrackingBeacons = (eventId: string): Promise<TrackingBeaconsResponse> => {
  return sdkRequest(
    `/events/${eventId}/tracking_beacons/`,
    undefined,
    process.env.NEXT_PUBLIC_PROXY_API_V3 ? '/e' : '',
  );
};

export const useTrackingBeacons = (): [TrackingBeacons | null, boolean] => {
  const { id: eventId } = useBasicInfo();
  const { data, isLoading } = useQuery({
    // eventId must be part of the key: the client keeps one QueryClient with a
    // 5-min staleTime across navigation, so a constant key would serve one
    // event's beacons (and consent flags) on another event's page.
    queryKey: ['trackingBeacons', eventId],
    queryFn: () => getTrackingBeacons(eventId),
  });
  // Memoize so consumers get a stable reference: transformTrackingBeacons builds
  // a new object each call, which would otherwise re-fire effects keyed on the
  // returned beacons on every re-render (e.g. duplicate pixel fires / Seeka
  // merges once user data resolves).
  const trackingBeacons = useMemo(() => transformTrackingBeacons(data), [data]);
  return [trackingBeacons, isLoading];
};
